import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import PortfolioTrackerHeader from "../header";
import PropertyCard from "./PropertyCard";
import { REIButton, REIDropDown, REIProgressSpinner } from "../../../components/ui";
import { useHttpGets } from "../../../services/httpService";
import { MEMBER_KEY, MyTrackerTabs, USER_DETAIL } from "../../../utils/constants";
import { ApiEndPoints } from "../../../utils/endpoints";
import ManageProperty from "./ManageProperty";
import { IMyProperties } from "../../../interface/myProperty";
import { useLocation } from "react-router-dom";
import AddProperty from "../AddProperty";
import EditProperty from "../AddProperty/EditProperty";
import { useFilterSelection } from "../FilterSelectionContext";
import { IMemberEntity } from "../../../interface/analysed";

const MyPropertiesIndex: React.FC<{ MyPropertyId: number }> = ({ MyPropertyId }) => {
  const location = useLocation();
  const { selectedEntityId } = useFilterSelection();
  const initialEntityIdRef = useRef<number>(selectedEntityId);
  const memberEntities = useSelector((state: RootState) => state.myTracker.memberEntities);
  const memberEntity = useRef<IMemberEntity[][]>();
  const [selectedEntity, setSelectedEntity] = useState(initialEntityIdRef.current.toString() || "0");
  const memberId = localStorage.getItem(MEMBER_KEY);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [propertiesData, setPropertiesData] = useState<any[]>([]);
  const [filteredProperties, setFilteredProperties] = useState<any[]>([]);
  const [isManagePropertyVisible, setIsManagePropertyVisible] = useState<boolean>(false);
  const [propertyDetails, setPropertyDetails] = useState<IMyProperties>({} as IMyProperties);
  const [showAddProperty, setShowAddProperty] = useState<boolean>(false);
  const [showEditProperty, setShowEditProperty] = useState<boolean>(false);
  const [portfolioTrackerId, setPortfolioTrackerId] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const state = location.state;
    if (state?.MyPropertyId) {
      handleGetPropertyDetails(state.MyPropertyId);
    }
  }, [MyPropertyId]);

  useEffect(() => {
    if (
      location.state &&
      !location.state.MyPropertyId &&
      location.state.AddNewProperty &&
      location.state.AddNewProperty === true
    ) {
      location.state = {};
      setShowAddProperty(true);
    } else if (
      location.state &&
      !location.state.MyPropertyId &&
      location.state.EditProperty &&
      location.state.EditProperty === true
    ) {
      setPortfolioTrackerId(location.state.portfolioTrackerId);
      location.state = {};
      setShowEditProperty(true);
    }
  }, [location.state]);

  const handleGetPropertyDetails = async (propertyId: number) => {
    try {
      useHttpGets(
        `${ApiEndPoints.tracker}PortfolioTracker/MyPortfolio?MemberId=${memberId}&userCountryCode=${memberDetail.Country}&PortfolioTrackerId=${propertyId}`
      ).then((response) => {
        if (response.error == null && response.status == 200) {
          const propertyDetails: IMyProperties = (response?.data as any).PortfolioTrackerDetailLabel;
          setPropertyDetails(propertyDetails);
          setIsManagePropertyVisible(true);
        }
      });
    } catch (error) {
      console.error("Error fetching property details:", error);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setMemberEntities();
    async function getPropertiesData() {
      useHttpGets(
        `${ApiEndPoints.tracker}PortfolioTracker/MyProperties?MemberId=${memberId}&userCountryCode=${memberDetail.Country}`
      )
        .then((response) => {
          const data: any[] = response?.data || [];
          setPropertiesData(() => data);
          selectedEntityId && selectedEntityId != 0
            ? setFilteredProperties(() => data.filter((property) => property.MemberEntityId == selectedEntityId))
            : setFilteredProperties(data);
        })
        .finally(() => {
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          console.log(error);
        });
    }
    getPropertiesData();
  }, [memberEntities, isManagePropertyVisible]);

  const handleAddProperty = (portfolioTrackerId?: number) => {
    setShowAddProperty(false);
    if (portfolioTrackerId !== undefined) {
      localStorage.removeItem("portfolioTrackerId");
      handleGetPropertyDetails(portfolioTrackerId);
    }
  };

  const setMemberEntities = () => {
    if (memberEntities) {
      let memeberEntitiesValues: any[] = [];
      memeberEntitiesValues.push({ EntityName: "All Entities", EntityValue: "0" });
      memberEntities.map((entities) => {
        memeberEntitiesValues.push({
          EntityName: entities.EntityName,
          EntityValue: entities.MemberEntityId.toString(),
        });
      });
      memberEntity.current = memeberEntitiesValues;
      selectedEntityId && selectedEntityId != 0
        ? setSelectedEntity(() => {
            return selectedEntityId.toString() || "0";
          })
        : setSelectedEntity(memeberEntitiesValues[0].EntityValue);
    }
  };

  const onEntityChange = (e: any) => {
    setSelectedEntity(() => e.value);
    if (e.value == "0") {
      setFilteredProperties(propertiesData);
    } else {
      setFilteredProperties(() => propertiesData.filter((property) => property.MemberEntityId == Number(e.value)));
    }
  };

  const handleManagePropertyClick = (propertyData: IMyProperties): void => {
    setPropertyDetails(propertyData);
    setIsManagePropertyVisible(true);
  };

  const handleRefresh = () => {
    setIsManagePropertyVisible(false);
  };

  return showAddProperty || showEditProperty ? (
    showEditProperty ? (
      <EditProperty
        onClose={(portfolioTrackerId?: number) => {
          setShowEditProperty(false);
          handleAddProperty(portfolioTrackerId);
        }}
        portfolioTrackerId={portfolioTrackerId}
      />
    ) : (
      <AddProperty onClose={handleAddProperty} />
    )
  ) : isLoading ? (
    <div className="content-center flex justify-center items-center min-h-[100px]">
      <REIProgressSpinner></REIProgressSpinner>
    </div>
  ) : (
    <div className="flex flex-col gap-4">
      <div hidden={isManagePropertyVisible || showAddProperty}>
        <PortfolioTrackerHeader tabIndex={MyTrackerTabs.MyProperties} tabName="My Properties"></PortfolioTrackerHeader>
        <div className="flex flex-row justify-between pl-4 mb-2">
          <div className="flex justify-start gap-4 ">
            <label className="self-center mr-3 " htmlFor="memberEntity">
              Show properties from entity:
            </label>

            {memberEntities && (
              <REIDropDown
                id="mpidx-drop-entity"
                data={memberEntity?.current || []}
                onSelectionChange={(e) => onEntityChange(e)}
                placeholder="Select"
                value={selectedEntity}
                optionValue="EntityValue"
                label="EntityName"
                className="w-64 align-middle"
              ></REIDropDown>
            )}
          </div>
          <div className="flex justify-end ">
            <REIButton
              id="mpidx-btn-addproperty"
              onClick={() => {
                setShowAddProperty(true);
              }}
              label="Add new property"
            ></REIButton>
          </div>
        </div>
        <div className="flex flex-col w-full gap-4">
          {Array.isArray(filteredProperties) &&
            filteredProperties.length > 0 &&
            filteredProperties.map((property) => {
              return (
                <PropertyCard
                  key={property.PortfolioTrackerId}
                  propertyData={property}
                  setPropertyData={handleManagePropertyClick}
                ></PropertyCard>
              );
            })}
        </div>
        {filteredProperties?.length == 0 && (
          <div className="flex content-start">
            You currently don't have any properties in this entity. You can add a new property to your portfolio and
            assign it here.
          </div>
        )}
      </div>
      {isManagePropertyVisible && (
        <div id="manage-property-card" hidden={!isManagePropertyVisible}>
          <ManageProperty handleRefresh={handleRefresh} propertyData={propertyDetails}></ManageProperty>
        </div>
      )}
    </div>
  );
};

export default MyPropertiesIndex;
